import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { DialogResult, DialogService, DspxCardSetBaseFormComponent, FormNavigationService, MessagePlaceholders, SpinnerService } from 'dspx-core';
import { API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { APP_DATA_KEYS, FILTER_ACTIONS, FILTER_QUESTION_CODES, FORM_ACTIONS, FORM_CUSTOM_ACTIONS, minWaitForDialogToCloseMs, NAV_TAB_NUMBERS, noFilterOptionCode, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, SECTION_CODES, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { AppDataService } from '../../services/app-data.service';
import { QuestionPath, ResponseParserService } from '../../services/response-parser.service';
import { ErrorService } from '../../services/error.service';
import { ExcelExportService } from '../../services/excel-export.service';
import { BaseCardSetListComponent } from "../base-card-set-list-component/base-card-set-list.component";
import { DopiFormGridFilterRequest } from '../../interfaces/dopi-form-grid-filter-request.interface';

@Component({
  selector: 'app-dopi-form-list',
  templateUrl: './dopi-form-list.component.html',
  styleUrl: './dopi-form-list.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class DopiFormListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  activeDialog: string;

  @ViewChild(DspxCardSetBaseFormComponent)
  private set _baseForm(value: DspxCardSetBaseFormComponent) {
    this.baseForm = value
  }

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    private errorService: ErrorService,
    private excelExportService: ExcelExportService,
    private formNavigationService: FormNavigationService) {
    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.dopiFormList,
      () => API_ENDPOINTS.dashboard.getDopiForms,
      true);
  }

  ngOnInit(): void {
    this.route.url.subscribe((url) => {
      if (url.length === 0) {
        // In the case where this component is being loaded as part of the default (empty) route, we want to force it
        // to the DOPI Form List route so that the menu will highlight the selected option.
        this.router.navigate(['/', ROUTE_CONSTANTS.dopiForm.list]);
      }
    });

    this.formNavigationService.onNavigation().subscribe(event => {
      setTimeout(() => {
        this.overrideHtmlTitleSetByDspxFramework();
      })
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
  }

  formLoad(event: any): void {
    this.loadGridDataOnFormLoad(this.getDefaultFilterRequest());
  }

  filterData(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.dopiFormListForm.dopiFormsFilter: {
        switch (event.action) {
          case FILTER_ACTIONS.common.applyFilters: {
            this.loadGridData(this.getFilterRequest());
            break;
          }
          case FILTER_ACTIONS.common.clearFilters: {
            this.loadGridData(this.getDefaultFilterRequest());
            break;
          }
        }
        break;
      }
    }
  }

  dialogButtonClicked(event: any): void {

    const activeDialog = this.activeDialog;
    delete this.activeDialog;

    switch (activeDialog) {
      case POP_UP_CODES.dopiFormList.requestFromExternalUser: {
        this.requestFromExternalUser(event);
        break;
      }
    }
  }

  actionButtonClicked(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.dopiFormListForm.dopiFormsPlaceholder: {
        switch (event.event.type) {
          case FORM_ACTIONS.dopiFormListForm.view: {
            const routePrefix = event.event.data.isApproverFormAccessible
              ? ROUTE_CONSTANTS.approverDopiForm.viewPrefix
              : ROUTE_CONSTANTS.dopiForm.viewPrefix;
            this.router.navigate(['/', routePrefix, event.event.data.id]);
            break;
          }
          case FORM_ACTIONS.dopiFormListForm.edit:
          case FORM_ACTIONS.dopiFormListForm.colLink: {
            const routePrefix = event.event.data.isApproverFormAccessible
              ? ROUTE_CONSTANTS.approverDopiForm.prefix
              : ROUTE_CONSTANTS.dopiForm.prefix;
            this.router.navigate(['/', routePrefix, event.event.data.id]);
            break;
          }
          case FORM_ACTIONS.dopiFormListForm.delete: {
            this.deleteDopiForm(event.event.data);
            break;
          }
          case FORM_ACTIONS.dopiFormListForm.duplicate: {
            this.duplicateDopiForm(event.event.data);
            break;
          }
          case FORM_ACTIONS.dopiFormListForm.custom: {
            switch (event.event.data.buttonType) {
              case FORM_CUSTOM_ACTIONS.dopiFormListForm.customAdd: {
                this.router.navigate(['/', ROUTE_CONSTANTS.dopiForm.prefix]);
                break;
              }
              case FORM_CUSTOM_ACTIONS.dopiFormListForm.bulkExport: {
                this.excelExportService.exportDopiFormsToXlsx({});
                break;
              }
              case FORM_CUSTOM_ACTIONS.dopiFormListForm.requestFromExternalUser: {
                this.openDialogForRequestFromExternalUser();
                break;
              }
            }
            break;
          }
        }
        break;
      }
    }
  }

  private getDefaultFilterRequest(): DopiFormGridFilterRequest {
    return {
      statusId: noFilterOptionCode,
      isCoiFlagged: noFilterOptionCode,
      isLatestDopiForDeclarer: noFilterOptionCode
    };
  }

  private getFilterRequest(): DopiFormGridFilterRequest {

    const filterQuestionPath: QuestionPath = [
      NAV_TAB_NUMBERS.dopiFormListForm.dopiForms,
      PAGE_NUMBERS.dopiFormListForm.dopiForms,
      SECTION_CODES.dopiFormListForm.resultSection,
      QUESTION_CODES.dopiFormListForm.dopiFormsFilter];

    const filterQuestionModel = this.getQuestionValue(this.model, ...filterQuestionPath);

    const filterRequest: DopiFormGridFilterRequest = {
      dopiId: filterQuestionModel?.[FILTER_QUESTION_CODES.dopiFormListForm.dopiId],
      submissionDateFrom: filterQuestionModel?.[FILTER_QUESTION_CODES.dopiFormListForm.submissionDateFrom],
      submissionDateTo: filterQuestionModel?.[FILTER_QUESTION_CODES.dopiFormListForm.submissionDateTo],      
      declarerId: filterQuestionModel?.[FILTER_QUESTION_CODES.dopiFormListForm.declarerId],
      approverId: filterQuestionModel?.[FILTER_QUESTION_CODES.dopiFormListForm.approverId],      
      statusId: filterQuestionModel?.[FILTER_QUESTION_CODES.dopiFormListForm.statusId],
      isCoiFlagged: filterQuestionModel?.[FILTER_QUESTION_CODES.dopiFormListForm.isCoiFlagged],
      isLatestDopiForDeclarer: filterQuestionModel?.[FILTER_QUESTION_CODES.dopiFormListForm.isLatestDopiForDeclarer]
    };

    return filterRequest;
  }

  private deleteDopiForm(dopiForm: any): void {
    this.registerSubscription(
      this.dialogService
        .showConfirm(POP_UP_CODES.dopiFormList.deleteConfirmation, { dopiId: dopiForm.dopiId })
        .pipe(take(1))
        .subscribe(data => {
          switch (data as DialogResult) {
            case DialogResult.ACCEPTED: {
              this.spinnerService.showSpinner();
              this.registerSubscription(
                this.appDataService.delete(`${API_ENDPOINTS.dopiForm.delete}/${dopiForm.id}`)
                  .pipe(take(1), this.errorService.errorHandler())
                  .subscribe(data => {
                    this.spinnerService.hideSpinner();

                    const isSuccess = !SERVER_ERROR_POP_UP_CODES.deleteDopiForm.includes(data.message);
                    this.activeDialog = isSuccess ? POP_UP_CODES.dopiFormList.deleteSuccessful : data.message;

                    const messagePlaceholders: MessagePlaceholders = { dopiId: dopiForm.dopiId };
                    this.dialogService.showDialog(this.activeDialog, null, messagePlaceholders);

                    if (isSuccess) {
                      this.loadGridData(this.getFilterRequest());
                    }
                  })
              );
              break;
            }
            case DialogResult.REJECTED: {
              break;
            }
          }
        })
    );
  }

  private duplicateDopiForm(dopiForm: any): void {
    this.dialogService
      .showConfirm(POP_UP_CODES.dopiFormList.duplicateConfirmation, { dopiId: dopiForm.dopiId })
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            this.spinnerService.showSpinner();
            this.registerSubscription(
              this.appDataService.post(`${API_ENDPOINTS.dopiForm.duplicate}/${dopiForm.id}`, null)
                .pipe(take(1), this.errorService.errorHandler())
                .subscribe(data => {
                  this.spinnerService.hideSpinner();

                  const isSuccess = !SERVER_ERROR_POP_UP_CODES.duplicateDopiForm.includes(data.message);
                  this.activeDialog = isSuccess ? POP_UP_CODES.dopiFormList.duplicateSuccessful : data.message;

                  const messagePlaceholders: MessagePlaceholders = { dopiId: dopiForm.dopiId, newDopiId: data.newDopiId };
                  this.dialogService.showDialog(this.activeDialog, null, messagePlaceholders);

                  if (isSuccess) {
                    this.loadGridData(this.getFilterRequest());
                  }
                })
            );
            break;
          }
          case DialogResult.REJECTED: {
            break;
          }
        }
      })
  }

  private openDialogForRequestFromExternalUser(model?: any): void {
    this.activeDialog = POP_UP_CODES.dopiFormList.requestFromExternalUser;
    this.dialogService.showDialog(this.activeDialog, model);
  }

  private requestFromExternalUser(model: any): void {
    this.registerSubscription(
      this.appDataService.post(API_ENDPOINTS.dopiForm.requestFromExternalUser, model)
        .pipe(take(1), this.errorService.errorHandler())
        .subscribe(data => {
          this.spinnerService.hideSpinner();

          const isSuccess = !SERVER_ERROR_POP_UP_CODES.requestDopiFormFromExternalUser.includes(data.message);
          this.activeDialog = isSuccess ? POP_UP_CODES.dopiFormList.requestFromExternalUserSuccessful : data.message;

          // To be able to open a new popup we need to wait until the existing popup is being closed, hence the setTimeout
          setTimeout(
            () => {
              if (isSuccess) {
                this.dialogService.showDialog(this.activeDialog);
                this.loadGridData(this.getFilterRequest());
              }
              else {
                const messagePlaceholders: MessagePlaceholders = { applicationUrl: this.appDataService.getAppData(APP_DATA_KEYS.applicationUrl) };
                this.registerSubscription(
                  this.dialogService
                    .showConfirm(this.activeDialog, messagePlaceholders)
                    .pipe(take(1))
                    .subscribe(data => {
                      setTimeout(
                        () => {
                          this.openDialogForRequestFromExternalUser(model);
                        },
                        minWaitForDialogToCloseMs);
                    }));
              }
            },
            minWaitForDialogToCloseMs);
        })
    );
  }
}
